import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../api/axiosInstance";
import clientAxiosInstance from "../api/clientAxiosInstance";
import { formatAccountNumberInput, formatRoutingNumberInput, validateRoutingNumber } from "../utils/validationHelpers";
import { BANK_ACCOUNT_TYPE_OPTIONS, BANK_LINK_TYPE_OPTIONS } from "../constants/accountConstants";
import { canonicalBankAccountType } from "../utils/bankAccounts";

function coerceIsPersonal(value) {
  if (value === true) return true;
  if (value === false) return false;
  if (typeof value === "string") return value.toLowerCase() === "true";
  return Boolean(value);
}

function EditBankAccountModal({
  isOpen,
  onClose,
  household,
  bankAccount,
  onSuccess,
  clientToken = null,
}) {
  const [formData, setFormData] = useState({
    bank_name: "",
    account_name: "",
    account_number: "",
    routing_number: "",
    account_type: "",
    link_type: "",
    is_personal: true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const initialValues = useMemo(() => {
    if (!bankAccount) return null;
    return {
      bank_name: bankAccount.bank_name || "",
      account_name: bankAccount.account_name || "",
      account_number:
        bankAccount.account_number !== undefined &&
        bankAccount.account_number !== null
          ? String(bankAccount.account_number)
          : "",
      routing_number:
        bankAccount.routing_number !== undefined &&
        bankAccount.routing_number !== null
          ? String(bankAccount.routing_number)
          : "",
      account_type: canonicalBankAccountType(bankAccount.account_type),
      link_type: bankAccount.link_type || "",
      is_personal: coerceIsPersonal(bankAccount.is_personal),
    };
  }, [bankAccount]);

  const hasUnsavedChanges = useMemo(() => {
    if (!isOpen || !initialValues) return false;
    const keys = Object.keys(initialValues);
    return keys.some((k) => {
      const a = initialValues[k];
      const b = formData[k];
      if (k === "is_personal") {
        return coerceIsPersonal(a) !== coerceIsPersonal(b);
      }
      if (k === "account_type") {
        return canonicalBankAccountType(a) !== canonicalBankAccountType(b);
      }
      return String(a ?? "") !== String(b ?? "");
    });
  }, [formData, initialValues, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setError("");
    if (!initialValues) return;
    setFormData(initialValues);
  }, [isOpen, initialValues]);

  const handleChange = (field, value) => {
    let sanitizedValue = value;
    if (field === "routing_number") {
      sanitizedValue = formatRoutingNumberInput(value);
    } else if (field === "account_number") {
      sanitizedValue = formatAccountNumberInput(value);
    }
    setFormData((prev) => ({ ...prev, [field]: sanitizedValue }));
    setError("");
  };

  const validateForm = () => {
    if (!formData.bank_name.trim()) {
      setError("Bank name is required");
      return false;
    }
    if (!formData.account_name.trim()) {
      setError("Account name is required");
      return false;
    }
    if (!formData.account_number.trim()) {
      setError("Account number is required");
      return false;
    }
    if (!formData.routing_number.trim()) {
      setError("Routing number is required");
      return false;
    }
    if (!validateRoutingNumber(formData.routing_number)) {
      setError("Please enter a valid 9-digit routing number");
      return false;
    }
    if (!formData.account_type) {
      setError("Account type is required");
      return false;
    }
    if (formData.bank_name.length > 100) {
      setError("Bank name must be 100 characters or less");
      return false;
    }
    if (formData.account_name.length > 100) {
      setError("Account name must be 100 characters or less");
      return false;
    }
    if (formData.account_number.length > 30) {
      setError("Account number must be 30 characters or less");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    if (!household?.id || !bankAccount?.id) return;

    setSaving(true);
    setError("");

    try {
      const payload = {
        bank_name: formData.bank_name.trim(),
        account_name: formData.account_name.trim(),
        account_number: formData.account_number.trim(),
        routing_number: formData.routing_number.trim(),
        account_type: canonicalBankAccountType(formData.account_type),
        link_type: formData.link_type || null,
        is_personal: formData.is_personal === true,
      };

      const endpoint = clientToken
        ? `/client/household/${household.id}/bank-account/${bankAccount.id}`
        : `/households/${household.id}/bank-account/${bankAccount.id}`;

      const response = await (clientToken
        ? clientAxiosInstance
        : axiosInstance
      ).put(
        endpoint,
        payload,
        clientToken
          ? {
              headers: {
                Authorization: `Bearer ${clientToken}`,
              },
            }
          : undefined,
      );

      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      if (typeof errorDetail === "string") {
        setError(errorDetail);
      } else if (Array.isArray(errorDetail)) {
        setError(
          errorDetail.map((e) => e.msg || e.message || JSON.stringify(e)).join(", "),
        );
      } else {
        setError("Failed to update bank account. Please try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setSaving(false);
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const routingInvalid =
    formData.routing_number.trim() !== "" &&
    !validateRoutingNumber(formData.routing_number);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Edit Bank Account</h2>
              <p className="text-sm text-gray-500 mt-1">
                Update details and save to apply changes.
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={saving}
              type="button"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {hasUnsavedChanges && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <svg
                  className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <p>
                  These bank account detail changes are unsaved. Click Save
                  Changes to apply.
                </p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.bank_name}
              onChange={(e) => handleChange("bank_name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., Chase Bank"
              disabled={saving}
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.account_name}
              onChange={(e) => handleChange("account_name", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., John & Jane Doe Joint Account"
              disabled={saving}
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.account_number}
              onChange={(e) => handleChange("account_number", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              placeholder="e.g., 1234567890"
              disabled={saving}
              maxLength={30}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Routing Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.routing_number}
              onChange={(e) => handleChange("routing_number", e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 ${
                routingInvalid ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., 021000021"
              disabled={saving}
              maxLength={9}
            />
            {routingInvalid && (
              <p className="text-red-500 text-xs mt-1">
                Enter a valid 9-digit routing number
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Type <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.account_type}
              onChange={(e) => handleChange("account_type", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={saving}
            >
              <option value="">Select account type</option>
              {BANK_ACCOUNT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.label}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link Type
            </label>
            <select
              value={formData.link_type}
              onChange={(e) => handleChange("link_type", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              disabled={saving}
            >
              <option value="">Select link type</option>
              {BANK_LINK_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Account Category <span className="text-red-500">*</span>
            </label>
            <div
              className={`flex bg-gray-100 rounded-lg p-1 w-full md:w-64 ${
                saving ? "opacity-60" : ""
              }`}
            >
              <button
                type="button"
                disabled={saving}
                onClick={() => handleChange("is_personal", true)}
                className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  formData.is_personal !== false
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Personal
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => handleChange("is_personal", false)}
                className={`flex-1 py-1.5 px-4 rounded-md text-sm font-medium transition-all duration-200 ${
                  formData.is_personal === false
                    ? "bg-white text-emerald-700 shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Business
              </button>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-xl border-t border-gray-200 flex justify-end gap-3">
            <button
              onClick={handleClose}
              type="button"
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              type="button"
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              disabled={saving || routingInvalid}
            >
              {saving ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EditBankAccountModal;

